import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, FileText, Loader2, ChevronUp, ChevronDown, Edit2, Lock, X, Save, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Governed Page Types (exact governance spec) ───────────────────────────────
const PAGE_TYPES = [
  { value: 'editorial_front_cover', label: 'Editorial Front Cover' },
  { value: 'inside_front_sponsor',  label: 'Inside Front Cover Sponsor' },
  { value: 'editors_note',          label: "Editor's Note" },
  { value: 'table_of_contents',     label: 'Table of Contents' },
  { value: 'recurring_department',  label: 'Recurring Department' },
  { value: 'editorial_article',     label: 'Editorial Article' },
  { value: 'sponsor_full_page',     label: 'Sponsor Full Page' },
  { value: 'elite_8',               label: 'The Elite 8' },
  { value: 'stay_smart',            label: 'Stay Smart' },
  { value: 'opportunity_signal',    label: 'Opportunity Signal: Month in Review' },
  { value: 'forward_brief',         label: 'Forward: Weekly Digest Highlights' },
  { value: 'opportunity_pass',      label: 'Opportunity Pass' },
  { value: 'advertiser_index',      label: 'Advertiser Index' },
  { value: 'inside_back_sponsor',   label: 'Inside Back Cover Sponsor' },
  { value: 'back_cover',            label: "Back Cover / Publisher's Choice" },
];

// Legacy display labels (for pages seeded before governance lock)
const LEGACY_TYPE_LABELS = {
  front_cover:          'Editorial Front Cover',
  editors_note_page:    "Editor's Note",
  in_focus:             'Editorial Article',
  executive_in_focus:   'Recurring Department',
  greg_feature:         'Recurring Department',
  gregs_crossroads:     'Recurring Department',
};

const getPageTypeLabel = (val) =>
  PAGE_TYPES.find(t => t.value === val)?.label ||
  LEGACY_TYPE_LABELS[val] ||
  val;

// ── Placement Protection ──────────────────────────────────────────────────────
const PROTECTED_PAGES = {
  1:  { role: 'Editorial Front Cover',        type: 'editorial_front_cover', color: '#065F46', bg: '#D1FAE5' },
  2:  { role: 'Inside Front Cover Sponsor',   type: 'inside_front_sponsor',  color: '#92400E', bg: '#FEF3C7' },
  37: { role: 'Advertiser Index',             type: 'advertiser_index',      color: '#5B21B6', bg: '#EDE9FE' },
  39: { role: 'Inside Back Cover Sponsor',    type: 'inside_back_sponsor',   color: '#92400E', bg: '#FEF3C7' },
  40: { role: "Back Cover / Publisher's Choice", type: 'back_cover',         color: '#1D4ED8', bg: '#DBEAFE' },
};

const isProtected = (pageNum) => !!PROTECTED_PAGES[pageNum];

// ── Page Status ───────────────────────────────────────────────────────────────
const PAGE_STATUS = {
  draft:     { label: 'Draft',     bg: 'bg-gray-100',   text: 'text-gray-600' },
  ready:     { label: 'Ready',     bg: 'bg-blue-100',   text: 'text-blue-700' },
  published: { label: 'Published', bg: 'bg-green-100',  text: 'text-green-700' },
};

const SPONSORSHIP_OPTIONS = [
  { value: 'editorial',  label: 'Editorial (No sponsor)' },
  { value: 'sponsored',  label: 'Sponsored Content' },
  { value: 'partner',    label: 'Partner Placement' },
  { value: 'house',      label: 'House Ad' },
];

// Ad layout governance: sponsor/ad page types only get the full-page vs two-half-page toggle
const SPONSOR_AD_PAGE_TYPES = ['inside_front_sponsor', 'sponsor_full_page', 'inside_back_sponsor'];

// ── Blank new page ────────────────────────────────────────────────────────────
const BLANK_PAGE = {
  page_number: '',
  page_type: 'editorial_article',
  title: '',
  blurb: '',
  section_name: '',
  page_status: 'draft',
  asset_type: 'image',
  anchor_tag: '',
  sponsorship_status: 'editorial',
  sponsor_name: '',
  content_item_id: '',
};

// ── Blank edit form ───────────────────────────────────────────────────────────
const pageToEditForm = (p) => ({
  page_number:        p.page_number,
  page_type:          p.page_type,
  title:              p.title || '',
  blurb:              p.blurb || '',
  body_html:          p.body_html || '',
  section_name:       p.section_name || '',
  department_name:    p.department_name || '',
  anchor_tag:         p.anchor_tag || '',
  sponsorship_status: p.sponsorship_status || 'editorial',
  sponsor_name:       p.sponsor_name || '',
  sponsor_disclosure: p.sponsor_disclosure || '',
  page_image_url:     p.page_image_url || '',
  thumbnail_url:      p.thumbnail_url || '',
  cta_label:          p.cta_label || '',
  cta_url:            p.cta_url || '',
  qr_destination_url: p.qr_destination_url || '',
  embed_url:          p.embed_url || '',
  video_url:          p.video_url || '',
  page_status:        p.page_status || 'draft',
  layout_template:    p.layout_template || '',
  is_member_only:     p.is_member_only || false,
  content_item_id:    p.content_item_id || '',
  ad_layout:               p.ad_layout || 'full_page',
  half_top_image_url:      p.half_top_image_url || '',
  half_top_cta_url:        p.half_top_cta_url || '',
  half_top_cta_label:      p.half_top_cta_label || '',
  half_top_sponsor_name:   p.half_top_sponsor_name || '',
  half_top_disclosure:     p.half_top_disclosure || '',
  half_bottom_image_url:    p.half_bottom_image_url || '',
  half_bottom_cta_url:      p.half_bottom_cta_url || '',
  half_bottom_cta_label:    p.half_bottom_cta_label || '',
  half_bottom_sponsor_name: p.half_bottom_sponsor_name || '',
  half_bottom_disclosure:   p.half_bottom_disclosure || '',
  half_top_thumbnail_url:   p.half_top_thumbnail_url || '',
  half_bottom_thumbnail_url: p.half_bottom_thumbnail_url || '',
});


// ── Sponsor page types that show fast entry ───────────────────────────────────
const SPONSOR_FAST_ENTRY_TYPES = ['sponsor_full_page', 'inside_front_sponsor', 'inside_back_sponsor'];

// ── SponsorFastEntry — inline fast entry component ────────────────────────────
function SponsorFastEntry({ page, issueId, onSaved }) {
  const [form, setForm] = useState({
    company_name:       page.sponsor_name        || '',
    logo_thumbnail_url: page.thumbnail_url       || '',
    ad_image_url:       page.page_image_url      || '',
    website_url:        page.cta_url             || '',
  });
  const [advertisers, setAdvertisers]       = useState([]);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [savedMsg, setSavedMsg]             = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [masterUpdating, setMasterUpdating] = useState(false);
  const [masterUpdatedMsg, setMasterUpdatedMsg] = useState(false);
  const saveTimerRef         = useRef(null);
  const masterUpdateTimerRef = useRef(null);
  // Fix 3: keep a ref always current with latest form to avoid stale closures
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);
  // Rename support: track the name that was originally loaded (from the page record or last
  // dropdown selection) so Update Master can find the right record even after the name is edited.
  const originalNameRef = useRef((page.sponsor_name || '').trim());

  const allFilled = !!(form.company_name && form.logo_thumbnail_url && form.ad_image_url && form.website_url);

  useEffect(() => {
    axios.get(`${API}/advertisers`).then(res => setAdvertisers(res.data)).catch(() => {});
  }, []);

  const filtered = advertisers.filter(a =>
    a.company_name.toLowerCase().includes(form.company_name.toLowerCase())
  );

  // Fix 3: handleSelect strictly overwrites ALL 4 fields, and also force-updates formRef
  // so any in-flight onBlur save uses the freshly-selected master values.
  // Also update originalNameRef so Update Master can find this record even if name is later edited.
  const handleSelect = (adv) => {
    const newForm = {
      company_name:       adv.company_name        || '',
      logo_thumbnail_url: adv.logo_thumbnail_url  || '',
      ad_image_url:       adv.ad_image_url        || '',
      website_url:        adv.website_url         || '',
    };
    formRef.current      = newForm;            // immediate — before React re-render
    originalNameRef.current = adv.company_name; // track which master record we're linked to
    setForm(newForm);
    setShowDropdown(false);
  };

  const PLACEHOLDER_NAMES = ['advertiser name', 'sponsor placement reserved', 'sponsor reserved', 'advertisement'];
  const isInvalidName = (n) => {
    const t = n.trim().toLowerCase();
    return t.length < 3 || PLACEHOLDER_NAMES.some(p => t === p);
  };

  // Fix 3: save reads from formRef.current so it always has the latest values
  // (avoids stale closure when called 150 ms after onBlur / dropdown select)
  const save = useCallback(async () => {
    const f = formRef.current;
    if (!f.company_name || isInvalidName(f.company_name)) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/magazine/${issueId}/pages/${page.id}`, {
        sponsor_name:   f.company_name,
        thumbnail_url:  f.logo_thumbnail_url,
        page_image_url: f.ad_image_url,
        cta_url:        f.website_url,
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Auto-create master record when ALL 4 fields are populated and not yet saved
      const allFour = f.company_name && f.logo_thumbnail_url && f.ad_image_url && f.website_url;
      if (allFour) {
        const existing = advertisers.find(
          a => a.company_name.toLowerCase() === f.company_name.trim().toLowerCase()
        );
        if (!existing) {
          try {
            const res = await axios.post(`${API}/advertisers`, {
              company_name:       f.company_name.trim(),
              logo_thumbnail_url: f.logo_thumbnail_url || null,
              ad_image_url:       f.ad_image_url       || null,
              website_url:        f.website_url        || null,
            }, { headers: { Authorization: `Bearer ${token}` } });
            setAdvertisers(prev =>
              [...prev, res.data].sort((a, b) => a.company_name.localeCompare(b.company_name))
            );
          } catch { /* 409 — already exists, skip */ }
        }
      }

      setSavedMsg(true);
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSavedMsg(false), 2000);
      onSaved?.();
    } catch {
      toast.error('Failed to save sponsor data');
    } finally {
      setSaving(false);
    }
  }, [issueId, page.id, advertisers, onSaved]);

  // Fix 2 (enhanced): overwrite the master advertisers record with the current 4 field values.
  // Matches by originalNameRef so a renamed company doesn't create a duplicate — it renames
  // the existing record. After success, originalNameRef is updated to the new name.
  const updateMaster = useCallback(async () => {
    const f = formRef.current;
    if (!f.company_name || isInvalidName(f.company_name)) return;
    setMasterUpdating(true);
    try {
      const token = localStorage.getItem('auth_token');
      const lookupName = originalNameRef.current || f.company_name.trim();
      // Find by original name (supports rename), fall back to current name
      const existing =
        advertisers.find(a => a.company_name.toLowerCase() === lookupName.toLowerCase()) ||
        advertisers.find(a => a.company_name.toLowerCase() === f.company_name.trim().toLowerCase());

      if (existing) {
        const res = await axios.put(`${API}/advertisers/${existing.id}`, {
          company_name:       f.company_name.trim(),
          logo_thumbnail_url: f.logo_thumbnail_url || null,
          ad_image_url:       f.ad_image_url       || null,
          website_url:        f.website_url        || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setAdvertisers(prev =>
          prev.map(a => a.id === existing.id ? res.data : a)
              .sort((a, b) => a.company_name.localeCompare(b.company_name))
        );
      } else {
        // No existing master record — create one
        const res = await axios.post(`${API}/advertisers`, {
          company_name:       f.company_name.trim(),
          logo_thumbnail_url: f.logo_thumbnail_url || null,
          ad_image_url:       f.ad_image_url       || null,
          website_url:        f.website_url        || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setAdvertisers(prev =>
          [...prev, res.data].sort((a, b) => a.company_name.localeCompare(b.company_name))
        );
      }
      // New name is now the canonical reference for future Update Master clicks
      originalNameRef.current = f.company_name.trim();
      setMasterUpdatedMsg(true);
      clearTimeout(masterUpdateTimerRef.current);
      masterUpdateTimerRef.current = setTimeout(() => setMasterUpdatedMsg(false), 2000);
    } catch {
      toast.error('Failed to update master advertiser');
    } finally {
      setMasterUpdating(false);
    }
  }, [advertisers]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    if (e.key === 'Escape') setShowDropdown(false);
  };

  return (
    <div
      className="px-5 py-3 border-t"
      style={{ background: '#FEFDF9', borderColor: '#F0EBE0' }}
      data-testid={`sponsor-fast-entry-${page.id}`}
    >
      {/* Header row with label + dot + Update Master button */}
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#92400E' }}>Sponsor Entry</span>
        <div
          className={`w-2 h-2 rounded-full flex-shrink-0 ${allFilled ? 'bg-green-500' : 'bg-red-400'}`}
          data-testid={`sponsor-completion-dot-${page.id}`}
          title={allFilled ? 'All sponsor fields complete' : 'Some sponsor fields are empty'}
        />
        {savedMsg && (
          <span className="text-xs font-semibold text-green-600" data-testid={`sponsor-saved-msg-${page.id}`}>
            ✓ Saved
          </span>
        )}
        {saving && <Loader2 size={11} className="animate-spin" style={{ color: '#92400E' }} />}
        <div className="flex-1" />
        {masterUpdatedMsg && (
          <span className="text-xs font-semibold text-emerald-600" data-testid={`master-updated-msg-${page.id}`}>
            Master Updated ✓
          </span>
        )}
        <button
          type="button"
          onClick={updateMaster}
          disabled={masterUpdating || !form.company_name}
          data-testid={`update-master-btn-${page.id}`}
          title="Overwrite the master advertisers record with the values below"
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderColor: '#92400E', color: '#92400E', background: 'transparent' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#92400E'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#92400E'; }}
        >
          {masterUpdating ? <Loader2 size={10} className="animate-spin" /> : null}
          Update Master
        </button>
      </div>

      {/* 4-field grid */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">

        {/* Field 1 — Company Name with typeahead */}
        <div className="relative">
          <label className="block text-xs text-gray-500 mb-0.5">Company Name</label>
          <input
            type="text"
            data-testid={`sponsor-company-name-${page.id}`}
            value={form.company_name}
            onChange={e => { setForm(f => ({ ...f, company_name: e.target.value })); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => { setTimeout(() => { setShowDropdown(false); save(); }, 150); }}
            onKeyDown={handleKeyDown}
            placeholder="Advertiser name..."
            className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
          />
          {showDropdown && filtered.length > 0 && (
            <div
              className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-gray-200 shadow-lg overflow-y-auto"
              style={{ maxHeight: 160 }}
              data-testid={`sponsor-typeahead-dropdown-${page.id}`}
            >
              {filtered.map(adv => (
                <button
                  key={adv.id}
                  type="button"
                  onMouseDown={() => handleSelect(adv)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-amber-50 flex items-center gap-2"
                >
                  {adv.logo_thumbnail_url && (
                    <img src={adv.logo_thumbnail_url} alt="" className="w-5 h-5 object-contain rounded flex-shrink-0" />
                  )}
                  <span className="truncate">{adv.company_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Field 2 — Badge / Logo URL */}
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Badge / Logo URL</label>
          <input
            type="text"
            data-testid={`sponsor-logo-url-${page.id}`}
            value={form.logo_thumbnail_url}
            onChange={e => setForm(f => ({ ...f, logo_thumbnail_url: e.target.value }))}
            onBlur={save}
            onKeyDown={handleKeyDown}
            placeholder="https://ik.imagekit.io/..."
            className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
          />
        </div>

        {/* Field 3 — Ad Image URL */}
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Ad Image URL</label>
          <input
            type="text"
            data-testid={`sponsor-ad-image-url-${page.id}`}
            value={form.ad_image_url}
            onChange={e => setForm(f => ({ ...f, ad_image_url: e.target.value }))}
            onBlur={save}
            onKeyDown={handleKeyDown}
            placeholder="https://ik.imagekit.io/..."
            className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
          />
        </div>

        {/* Field 4 — Website URL */}
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Website URL</label>
          <input
            type="text"
            data-testid={`sponsor-website-url-${page.id}`}
            value={form.website_url}
            onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))}
            onBlur={save}
            onKeyDown={handleKeyDown}
            placeholder="https://advertiser.com"
            className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export const MagazinePageManager = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [issue, setIssue]           = useState(null);
  const [pages, setPages]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPage, setNewPage]       = useState(BLANK_PAGE);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(null);
  const [editingPageId, setEditingPageId] = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  // Map of page.id → { title, slug } for resolved content item links
  const [linkedItemInfo, setLinkedItemInfo] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [issuesRes, pagesRes] = await Promise.all([
        axios.get(`${API}/magazine`),
        axios.get(`${API}/magazine/${id}/pages`),
      ]);
      setIssue(issuesRes.data.find(i => i.id === id) || null);
      setPages(pagesRes.data);
    } catch {
      toast.error('Failed to load issue data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) { navigate('/admin/login'); return; }
    loadData();
  }, [loadData, navigate]);

  // ── Add page ──
  const handleAddPage = async (e) => {
    e.preventDefault();
    if (!newPage.page_number || !newPage.page_type) {
      toast.error('Page number and type are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const payload = { ...newPage, page_number: parseInt(newPage.page_number) };
      await axios.post(`${API}/magazine/${id}/pages`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Page ${newPage.page_number} added`);
      setNewPage(BLANK_PAGE);
      setShowAddForm(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add page');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete page ──
  const handleDeletePage = async (pageId, pageNum) => {
    if (isProtected(pageNum)) {
      toast.error(`Page ${pageNum} is a protected placement and cannot be deleted.`);
      return;
    }
    if (!window.confirm(`Delete page ${pageNum}? This cannot be undone.`)) return;
    setDeleting(pageId);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/magazine/${id}/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Page ${pageNum} deleted`);
      setPages(prev => prev.filter(p => p.id !== pageId));
    } catch {
      toast.error('Failed to delete page');
    } finally {
      setDeleting(null);
    }
  };

  // ── Open edit form ──
  const openEdit = (page) => {
    if (editingPageId === page.id) { setEditingPageId(null); return; }
    setEditingPageId(page.id);
    setEditForm(pageToEditForm(page));
    // Resolve linked content item details for display
    if (page.content_item_id && !linkedItemInfo[page.id]) {
      const token = localStorage.getItem('auth_token');
      axios.get(`${API}/content/${page.content_item_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setLinkedItemInfo(prev => ({
          ...prev,
          [page.id]: { title: res.data.title, slug: res.data.slug }
        }));
      }).catch(() => {});
    }
  };

  // ── Save edit ──
  const handleSaveEdit = async (pageId) => {
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        ...editForm,
        page_number: parseInt(editForm.page_number) || undefined,
      };
      await axios.put(`${API}/magazine/${id}/pages/${pageId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Page saved');
      setEditingPageId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save page');
    } finally {
      setSavingEdit(false);
    }
  };

  const ef = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setEditForm(prev => ({ ...prev, [key]: val }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="page-manager-loading">
        <Loader2 className="animate-spin text-[#0E2E59]" size={36} />
      </div>
    );
  }

  return (
    <AdminLayout title="Magazine Manager" breadcrumb="Page Manager">
    <div className="min-h-screen bg-gray-50" data-testid="page-manager">
      <div className="container-custom py-8 max-w-5xl">

        {/* Issue summary */}
        {issue && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6 flex items-start gap-4"
            data-testid="issue-summary">
            <div className="w-12 h-16 rounded-lg flex-shrink-0 flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#081D30,#006E6A)' }}>
              <span className="text-white font-black text-xs">OP</span>
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-lg text-gray-900">{issue.title}</h2>
              <p className="text-sm text-gray-500">
                {issue.issue_date} · {issue.page_count} pages ·{' '}
                <span className={`font-semibold capitalize ${
                  issue.issue_status === 'published' ? 'text-green-600' :
                  issue.issue_status === 'review' ? 'text-blue-600' :
                  issue.issue_status === 'archived' ? 'text-red-500' : 'text-gray-500'
                }`}>{issue.issue_status}</span>
              </p>
              {issue.issue_theme && <p className="text-xs text-gray-400 italic mt-1">{issue.issue_theme}</p>}
            </div>
            {/* Protected pages legend */}
            <div className="hidden sm:flex flex-col gap-1.5 text-xs ml-4" data-testid="protected-legend">
              <p className="font-bold text-gray-500 text-xs uppercase tracking-wider mb-0.5">Protected</p>
              {Object.entries(PROTECTED_PAGES).map(([pg, info]) => (
                <span key={pg} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded"
                  style={{ background: info.bg, color: info.color }}>
                  <Lock size={10} /> p.{pg}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Add page / pages heading */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900" data-testid="pages-heading">
            Pages ({pages.length})
          </h3>
          <button onClick={() => { setShowAddForm(!showAddForm); setEditingPageId(null); }}
            className="inline-flex items-center gap-2 bg-[#0E2E59] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#1a4d8f] transition-colors text-sm"
            data-testid="add-page-btn">
            {showAddForm ? <ChevronUp size={16} /> : <Plus size={16} />}
            {showAddForm ? 'Hide Form' : 'Add Page'}
          </button>
        </div>

        {/* Add page form */}
        {showAddForm && (
          <form onSubmit={handleAddPage}
            className="bg-white rounded-xl border border-[#0E2E59]/20 shadow-sm p-5 mb-6 space-y-4"
            data-testid="add-page-form">
            <h4 className="font-bold text-[#0E2E59]">Add New Page</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <LabeledField label="Page Number *">
                <input type="number" min={1} max={100} value={newPage.page_number}
                  onChange={e => setNewPage(p => ({ ...p, page_number: e.target.value }))}
                  className={INPUT_CLS} required data-testid="new-page-number" />
              </LabeledField>
              <LabeledField label="Page Type *">
                <select value={newPage.page_type}
                  onChange={e => setNewPage(p => ({ ...p, page_type: e.target.value }))}
                  className={INPUT_CLS} data-testid="new-page-type">
                  {PAGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </LabeledField>
              <LabeledField label="Status">
                <select value={newPage.page_status}
                  onChange={e => setNewPage(p => ({ ...p, page_status: e.target.value }))}
                  className={INPUT_CLS} data-testid="new-page-status">
                  <option value="draft">Draft</option>
                  <option value="ready">Ready</option>
                  <option value="published">Published</option>
                </select>
              </LabeledField>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LabeledField label="Title">
                <input type="text" value={newPage.title}
                  onChange={e => setNewPage(p => ({ ...p, title: e.target.value }))}
                  placeholder="Page headline..." className={INPUT_CLS} data-testid="new-page-title" />
              </LabeledField>
              <LabeledField label="Section / Department">
                <input type="text" value={newPage.section_name}
                  onChange={e => setNewPage(p => ({ ...p, section_name: e.target.value, department_name: e.target.value }))}
                  placeholder="e.g. Departments, Cover Story" className={INPUT_CLS} data-testid="new-page-section" />
              </LabeledField>
              <LabeledField label="Blurb (TOC description)" className="sm:col-span-2">
                <input type="text" value={newPage.blurb}
                  onChange={e => setNewPage(p => ({ ...p, blurb: e.target.value }))}
                  placeholder="Brief description for TOC..." className={INPUT_CLS} data-testid="new-page-blurb" />
              </LabeledField>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={saving}
                className="inline-flex items-center gap-2 bg-[#0E2E59] text-white px-5 py-2 rounded-lg font-semibold hover:bg-[#1a4d8f] transition-colors text-sm disabled:opacity-60"
                data-testid="save-page-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                {saving ? 'Adding...' : 'Add Page'}
              </button>
              <button type="button" onClick={() => { setShowAddForm(false); setNewPage(BLANK_PAGE); }}
                className="text-sm text-gray-500 hover:text-gray-800 px-3" data-testid="cancel-add-page-btn">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Pages list */}
        {pages.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100" data-testid="empty-pages">
            <FileText size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No pages yet</p>
            <p className="text-gray-400 text-sm mt-1">Click "Add Page" to start building this issue.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="pages-list">
            {pages.map((page) => {
              const st = PAGE_STATUS[page.page_status] || PAGE_STATUS.draft;
              const prot = PROTECTED_PAGES[page.page_number];
              const isEditing = editingPageId === page.id;
              return (
                <div key={page.id} className="rounded-xl border shadow-sm transition-shadow"
                  style={{ borderColor: isEditing ? '#0E2E59' : prot ? prot.bg : '#F3F4F6', boxShadow: isEditing ? '0 0 0 2px rgba(14,46,89,.15)' : undefined }}
                  data-testid="page-row">

                  {/* ── Row header ── */}
                  <div className="bg-white px-5 py-3.5 flex items-center gap-4">
                    {/* Page number badge */}
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center font-black text-sm flex-shrink-0"
                      style={{ background: prot ? prot.bg : '#F8F6F0', color: prot ? prot.color : '#0E2E59' }}>
                      {prot ? <Lock size={14} /> : page.page_number}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm" data-testid="page-title">
                          {page.title || getPageTypeLabel(page.page_type)}
                        </span>
                        {prot && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded"
                            style={{ background: prot.bg, color: prot.color }}
                            data-testid="protected-badge">
                            Protected · p.{page.page_number}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${st.bg} ${st.text}`}
                          data-testid="page-status-badge">
                          {st.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                        <span>p.{page.page_number}</span>
                        <span>·</span>
                        <span>{getPageTypeLabel(page.page_type)}</span>
                        {page.section_name && <><span>·</span><span>{page.section_name}</span></>}
                        {page.content_item_id && (
                          <span className="inline-flex items-center gap-1 font-semibold text-[#0E2E59] bg-blue-50 px-2 py-0.5 rounded"
                            data-testid="content-item-linked-badge">
                            <FileText size={10} /> Linked article
                          </span>
                        )}
                        {page.sponsorship_status && page.sponsorship_status !== 'editorial' && (
                          <span className="font-semibold text-yellow-600">· {page.sponsorship_status}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Completion dot for sponsor pages */}
                      {SPONSOR_FAST_ENTRY_TYPES.includes(page.page_type) && (() => {
                        const filled = !!(page.sponsor_name && page.thumbnail_url && page.page_image_url && page.cta_url);
                        return (
                          <div
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${filled ? 'bg-green-500' : 'bg-red-400'}`}
                            data-testid={`row-completion-dot-${page.id}`}
                            title={filled ? 'Sponsor fields complete' : 'Sponsor fields incomplete'}
                          />
                        );
                      })()}
                      <button onClick={() => openEdit(page)}
                        className={`p-2 rounded-lg transition-colors ${isEditing ? 'bg-[#0E2E59] text-white' : 'text-gray-400 hover:text-[#0E2E59] hover:bg-gray-50'}`}
                        title="Edit page" data-testid="edit-page-btn">
                        {isEditing ? <X size={16} /> : <Edit2 size={16} />}
                      </button>
                      <button onClick={() => handleDeletePage(page.id, page.page_number)}
                        disabled={deleting === page.id || !!prot}
                        className="p-2 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={prot ? 'Protected placement — cannot delete' : 'Delete page'}
                        data-testid="delete-page-btn">
                        {deleting === page.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* ── Sponsor Fast Entry (always visible for sponsor page types) ── */}
                  {SPONSOR_FAST_ENTRY_TYPES.includes(page.page_type) && (
                    <SponsorFastEntry
                      key={`sfe-${page.id}`}
                      page={page}
                      issueId={id}
                      onSaved={loadData}
                    />
                  )}

                  {/* ── Inline edit form ── */}
                  {isEditing && (
                    <div className="bg-gray-50 border-t border-[#0E2E59]/15 px-5 py-5 space-y-4"
                      data-testid={`edit-form-${page.id}`}>

                      {prot && (
                        <div className="flex items-start gap-2 p-3 rounded-lg text-sm"
                          style={{ background: prot.bg, color: prot.color }} data-testid="protected-warning">
                          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                          <span><strong>Protected placement — p.{page.page_number}: {prot.role}.</strong> You can edit content, but changing the page number or role requires editorial approval.</span>
                        </div>
                      )}

                      {/* Row 1: page number, type, status */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <LabeledField label="Page Number">
                          <input type="number" min={1} max={100} value={editForm.page_number}
                            onChange={ef('page_number')} className={INPUT_CLS}
                            data-testid="edit-page-number" />
                        </LabeledField>
                        <LabeledField label="Page Type">
                          <select value={editForm.page_type} onChange={ef('page_type')}
                            className={INPUT_CLS} data-testid="edit-page-type">
                            {PAGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </LabeledField>
                        <LabeledField label="Status">
                          <select value={editForm.page_status} onChange={ef('page_status')}
                            className={INPUT_CLS} data-testid="edit-page-status">
                            <option value="draft">Draft</option>
                            <option value="ready">Ready</option>
                            <option value="published">Published</option>
                          </select>
                        </LabeledField>
                      </div>

                      {/* Row 2: Taxonomy */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <LabeledField label="Section / Department">
                          <input type="text" value={editForm.section_name} onChange={ef('section_name')}
                            placeholder="Departments, Cover Story..." className={INPUT_CLS}
                            data-testid="edit-section" />
                        </LabeledField>
                        <LabeledField label="Anchor Tag">
                          <input type="text" value={editForm.anchor_tag} onChange={ef('anchor_tag')}
                            placeholder="e.g. stay-smart-q1" className={INPUT_CLS}
                            data-testid="edit-anchor" />
                        </LabeledField>
                        <LabeledField label="Layout Template">
                          <input type="text" value={editForm.layout_template} onChange={ef('layout_template')}
                            placeholder="e.g. full-bleed, two-col" className={INPUT_CLS}
                            data-testid="edit-layout" />
                        </LabeledField>
                      </div>

                      {/* Row 3: Headline + Dek */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <LabeledField label="Headline / Title">
                          <input type="text" value={editForm.title} onChange={ef('title')}
                            placeholder="Page headline..." className={INPUT_CLS}
                            data-testid="edit-title" />
                        </LabeledField>
                        <LabeledField label="Dek / Summary (blurb)">
                          <input type="text" value={editForm.blurb} onChange={ef('blurb')}
                            placeholder="TOC summary line..." className={INPUT_CLS}
                            data-testid="edit-blurb" />
                        </LabeledField>
                      </div>

                      {/* Row 4: Body */}
                      <LabeledField label="Body / Content Block">
                        <textarea value={editForm.body_html} onChange={ef('body_html')} rows={3}
                          placeholder="HTML or plain text body content..."
                          className={INPUT_CLS + ' resize-none'} data-testid="edit-body" />
                      </LabeledField>

                      {/* Row 5: Sponsor */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <LabeledField label="Sponsorship Status">
                          <select value={editForm.sponsorship_status} onChange={ef('sponsorship_status')}
                            className={INPUT_CLS} data-testid="edit-sponsorship-status">
                            {SPONSORSHIP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </LabeledField>
                        <LabeledField label="Sponsor Label / Name">
                          <input type="text" value={editForm.sponsor_name} onChange={ef('sponsor_name')}
                            placeholder="Company name..." className={INPUT_CLS}
                            data-testid="edit-sponsor-name" />
                        </LabeledField>
                        <LabeledField label="Sponsor Disclosure">
                          <input type="text" value={editForm.sponsor_disclosure} onChange={ef('sponsor_disclosure')}
                            placeholder="e.g. Sponsored content by..." className={INPUT_CLS}
                            data-testid="edit-disclosure" />
                        </LabeledField>
                      </div>

                      {/* Row 6: Assets */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <LabeledField label="Advertiser Ad Creative URL">
                          <input type="text" value={editForm.page_image_url} onChange={ef('page_image_url')}
                            placeholder="https://ik.imagekit.io/..." className={INPUT_CLS}
                            data-testid="edit-image-url" />
                          <p className="text-xs text-gray-400 mt-1">ImageKit URL for the full advertisement artwork displayed on the magazine page.</p>
                        </LabeledField>
                        <LabeledField label="Advertiser Badge / Logo URL">
                          <input type="text" value={editForm.thumbnail_url} onChange={ef('thumbnail_url')}
                            placeholder="https://ik.imagekit.io/..." className={INPUT_CLS}
                            data-testid="edit-thumbnail" />
                          <p className="text-xs text-gray-400 mt-1">ImageKit URL for the advertiser's compact logo or badge used in the Table of Contents and Advertisers Index.</p>
                        </LabeledField>
                      </div>

                      {/* Row 6b: Ad Layout governance — sponsor/ad page types only */}
                      {SPONSOR_AD_PAGE_TYPES.includes(editForm.page_type) && (
                        <div className="rounded-lg border border-[#0E2E59]/15 bg-white p-4 space-y-3" data-testid="ad-layout-panel">
                          <label className="flex items-center gap-2 text-sm font-medium text-[#0E2E59]">
                            <input
                              type="checkbox"
                              data-testid="edit-ad-layout-toggle"
                              checked={editForm.ad_layout === 'two_half_page'}
                              onChange={e => setEditForm(prev => ({ ...prev, ad_layout: e.target.checked ? 'two_half_page' : 'full_page' }))}
                            />
                            Two half-page ads on this page
                          </label>
                          <p className="text-xs text-gray-500">Default is one full 842:1191 portrait page ad. Enable only for legacy/shared-page advertisers.</p>

                          {editForm.ad_layout === 'two_half_page' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-[#0E2E59]/10">
                              <div className="space-y-2" data-testid="half-top-fields">
                                <div className="text-xs font-semibold uppercase tracking-wide text-[#0E2E59]/70">Top Half Ad</div>
                                <LabeledField label="Top Half Image URL">
                                  <input type="text" value={editForm.half_top_image_url} onChange={ef('half_top_image_url')}
                                    placeholder="https://ik.imagekit.io/..." className={INPUT_CLS} data-testid="edit-half-top-image-url" />
                                </LabeledField>
                                <LabeledField label="Top Half CTA URL">
                                  <input type="text" value={editForm.half_top_cta_url} onChange={ef('half_top_cta_url')}
                                    placeholder="https://..." className={INPUT_CLS} data-testid="edit-half-top-cta-url" />
                                </LabeledField>
                                <LabeledField label="Top Half Sponsor Name">
                                  <input type="text" value={editForm.half_top_sponsor_name} onChange={ef('half_top_sponsor_name')}
                                    placeholder="Company name..." className={INPUT_CLS} data-testid="edit-half-top-sponsor-name" />
                                </LabeledField>
                                <LabeledField label="Top Half Advertiser Badge / Logo URL">
                                  <input type="text" value={editForm.half_top_thumbnail_url} onChange={ef('half_top_thumbnail_url')}
                                    placeholder="https://ik.imagekit.io/..." className={INPUT_CLS} data-testid="edit-half-top-thumbnail-url" />
                                  <p className="text-xs text-gray-400 mt-1">Compact logo shown in the Advertisers Index.</p>
                                </LabeledField>
                              </div>
                              <div className="space-y-2" data-testid="half-bottom-fields">
                                <div className="text-xs font-semibold uppercase tracking-wide text-[#0E2E59]/70">Bottom Half Ad</div>
                                <LabeledField label="Bottom Half Image URL">
                                  <input type="text" value={editForm.half_bottom_image_url} onChange={ef('half_bottom_image_url')}
                                    placeholder="https://ik.imagekit.io/..." className={INPUT_CLS} data-testid="edit-half-bottom-image-url" />
                                </LabeledField>
                                <LabeledField label="Bottom Half CTA URL">
                                  <input type="text" value={editForm.half_bottom_cta_url} onChange={ef('half_bottom_cta_url')}
                                    placeholder="https://..." className={INPUT_CLS} data-testid="edit-half-bottom-cta-url" />
                                </LabeledField>
                                <LabeledField label="Bottom Half Sponsor Name">
                                  <input type="text" value={editForm.half_bottom_sponsor_name} onChange={ef('half_bottom_sponsor_name')}
                                    placeholder="Company name..." className={INPUT_CLS} data-testid="edit-half-bottom-sponsor-name" />
                                </LabeledField>
                                <LabeledField label="Bottom Half Advertiser Badge / Logo URL">
                                  <input type="text" value={editForm.half_bottom_thumbnail_url} onChange={ef('half_bottom_thumbnail_url')}
                                    placeholder="https://ik.imagekit.io/..." className={INPUT_CLS} data-testid="edit-half-bottom-thumbnail-url" />
                                  <p className="text-xs text-gray-400 mt-1">Compact logo shown in the Advertisers Index.</p>
                                </LabeledField>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Row 7: CTAs */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <LabeledField label="CTA Label">
                          <input type="text" value={editForm.cta_label} onChange={ef('cta_label')}
                            placeholder="e.g. Apply Now" className={INPUT_CLS}
                            data-testid="edit-cta-label" />
                        </LabeledField>
                        <LabeledField label="CTA URL">
                          <input type="text" value={editForm.cta_url} onChange={ef('cta_url')}
                            placeholder="https://..." className={INPUT_CLS}
                            data-testid="edit-cta-url" />
                        </LabeledField>
                        <LabeledField label="QR Destination URL">
                          <input type="text" value={editForm.qr_destination_url} onChange={ef('qr_destination_url')}
                            placeholder="https://..." className={INPUT_CLS}
                            data-testid="edit-qr-url" />
                        </LabeledField>
                      </div>

                      {/* Row 8: Media */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <LabeledField label="Video URL (placeholder)">
                          <input type="text" value={editForm.video_url} onChange={ef('video_url')}
                            placeholder="https://..." className={INPUT_CLS}
                            data-testid="edit-video-url" />
                        </LabeledField>
                        <LabeledField label="Embed URL (placeholder)">
                          <input type="text" value={editForm.embed_url} onChange={ef('embed_url')}
                            placeholder="https://..." className={INPUT_CLS}
                            data-testid="edit-embed-url" />
                        </LabeledField>
                      </div>

                      {/* Row 9: Linked Content Item */}
                      <div className="border border-blue-100 rounded-lg p-4 bg-blue-50/40"
                        data-testid="linked-content-item-section">
                        <p className="text-xs font-bold text-[#0E2E59] mb-2 uppercase tracking-wide">
                          Linked Content Item
                        </p>
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                              Content Item ID
                            </label>
                            <input type="text" value={editForm.content_item_id}
                              onChange={ef('content_item_id')}
                              placeholder="Paste content_items ID from Editorial Hub..."
                              className={INPUT_CLS}
                              data-testid="edit-content-item-id" />
                          </div>
                        </div>
                        {linkedItemInfo[page.id] ? (
                          <div className="mt-2 flex items-center gap-2 text-sm">
                            <FileText size={14} className="text-[#0E2E59] flex-shrink-0" />
                            <span className="font-medium text-gray-800 truncate">
                              {linkedItemInfo[page.id].title}
                            </span>
                            <a href={`/article/${linkedItemInfo[page.id].slug}`}
                              target="_blank" rel="noreferrer"
                              className="text-xs text-[#0E2E59] underline underline-offset-2 flex-shrink-0 hover:opacity-75"
                              data-testid="view-linked-article-link">
                              View article
                            </a>
                          </div>
                        ) : editForm.content_item_id ? (
                          <p className="mt-1.5 text-xs text-gray-500">Save to resolve article details.</p>
                        ) : (
                          <p className="mt-1.5 text-xs text-gray-400">
                            Link this page to a published content item. Paste the ID from the Editorial Content Hub.
                          </p>
                        )}
                      </div>

                      {/* Row 10: Flags */}
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id={`member-only-${page.id}`}
                          checked={editForm.is_member_only} onChange={ef('is_member_only')}
                          className="w-4 h-4 rounded" data-testid="edit-member-only" />
                        <label htmlFor={`member-only-${page.id}`} className="text-sm font-medium text-gray-700">
                          Member-only page (requires subscription)
                        </label>
                      </div>

                      {/* Save / Cancel */}
                      <div className="flex gap-3 pt-1">
                        <button onClick={() => handleSaveEdit(page.id)} disabled={savingEdit}
                          className="inline-flex items-center gap-2 bg-[#0E2E59] text-white px-5 py-2 rounded-lg font-semibold hover:bg-[#1a4d8f] transition-colors text-sm disabled:opacity-60"
                          data-testid="save-edit-btn">
                          {savingEdit ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                          {savingEdit ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button onClick={() => setEditingPageId(null)}
                          className="text-sm text-gray-500 hover:text-gray-800 px-3"
                          data-testid="cancel-edit-btn">
                          Discard
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
};

// ── Small helpers ─────────────────────────────────────────────────────────────
const INPUT_CLS = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2E59] bg-white';

const LabeledField = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);
